import { CHART_COLOR_NAMES, DataPoint, DataSeries } from '@factorialco/deprecated-design-system'
import { isAfter, isBefore, isSameDay, parseISO } from 'date-fns'
import {
  InsightsHeadcountFragment,
  InsightsJoinerFragment,
  InsightsLeaverFragment,
} from 'generated/resources/graphql'

import { parseBackendDate } from 'modules/core/components/DateNavigator/dateFormatUtils'
import { dateToString } from 'modules/core/lib/dates'

import { CycleDateRange } from 'modules/insights/hooks/useCycleDateRange'

export const getColorHeadCount = () => CHART_COLOR_NAMES[0]
export const getColorJoiners = () => CHART_COLOR_NAMES[1]
export const getColorLeavers = () => CHART_COLOR_NAMES[2]

export type HeadcountEvolutionData = [DataSeries<Date>, DataSeries<Date>, DataSeries<Date>]

const processMonthlyData = (
  nodes: Array<{ endDate: string; value: number }>,
  cycleDateRange: CycleDateRange
) => {
  return nodes
    .map((item) => ({ endDate: parseISO(item.endDate), value: item.value }))
    .sort((a, b) => a.endDate.valueOf() - b.endDate.valueOf())
    .flatMap((item) => {
      if (isBefore(item.endDate, cycleDateRange.startDate)) {
        return []
      }

      if (isAfter(item.endDate, cycleDateRange.endDate)) {
        return []
      }

      return {
        x: item.endDate,
        y: item.value,
      }
    })
}

export const headcountEvolutionData = (
  headcountEvolutionByMonth: InsightsHeadcountFragment[],
  leavers: InsightsLeaverFragment[],
  joiners: InsightsJoinerFragment[],
  cycleDateRange: CycleDateRange
): HeadcountEvolutionData => {
  return [
    {
      id: `headcount-${cycleDateRange.cycle}`,
      data: processMonthlyData(
        headcountEvolutionByMonth.map((e) => ({
          ...e,
          value: e.headcount,
        })),
        cycleDateRange
      ),
    },
    {
      id: `joiners-${cycleDateRange.cycle}`,
      data: processMonthlyData(
        joiners.map((e) => ({ ...e, value: e.joiners })),
        cycleDateRange
      ),
    },
    {
      id: `leavers-${cycleDateRange.cycle}`,
      data: processMonthlyData(
        leavers.map((e) => ({ ...e, value: e.leavers })),
        cycleDateRange
      ),
    },
  ]
}

export type HeadcountEvolutionMonthData = {
  startDate: Date
  endDate: Date
  headcount: number
  joiners: number
  leavers: number
}

export function findDataFor(
  headcountEvolution: InsightsHeadcountFragment[],
  data: HeadcountEvolutionData,
  xCoordinate: Date
): HeadcountEvolutionMonthData | undefined {
  const headcountSeries = data[0].data
  const joinersSeries = data[1].data
  const leaversSeries = data[2].data

  const coordinateRange = headcountEvolution.find(
    (element) => element.endDate === dateToString(xCoordinate, 'backend')
  )

  if (!coordinateRange) {
    return undefined
  }

  const findDataPoint = (series: DataPoint<Date>[]) => {
    return series.find((item) => isSameDay(item.x, xCoordinate))
  }

  return {
    startDate: parseBackendDate(coordinateRange.startDate),
    endDate: parseBackendDate(coordinateRange.endDate),
    headcount: findDataPoint(headcountSeries)?.y || 0,
    joiners: findDataPoint(joinersSeries)?.y || 0,
    leavers: findDataPoint(leaversSeries)?.y || 0,
  }
}
